import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { NativeBiometric } from '@capgo/capacitor-native-biometric'

const BIOMETRIC_EMAIL_KEY = 'biometric_email'
const BIOMETRIC_TOKEN_KEY = 'biometric_refresh_token'

export function useBiometricAuth() {
  const { public: { appName } } = useRuntimeConfig()
  const isAvailable = ref(false)

  async function checkAvailability() {
    if (!Capacitor.isNativePlatform()) {
      isAvailable.value = false
      return false
    }
    try {
      const { isAvailable: avail } = await NativeBiometric.isAvailable()
      isAvailable.value = avail
      return avail
    } catch {
      isAvailable.value = false
      return false
    }
  }

  /** Verifica se o usuário ativou biometria */
  async function isRegistered(): Promise<boolean> {
    const { value } = await Preferences.get({ key: BIOMETRIC_EMAIL_KEY })
    return !!value
  }

  /** Registra biometria após login bem-sucedido */
  async function register(email: string, refreshToken: string) {
    await Preferences.set({ key: BIOMETRIC_EMAIL_KEY, value: email })
    await Preferences.set({ key: BIOMETRIC_TOKEN_KEY, value: refreshToken })
  }

  /** Desativa biometria */
  async function unregister() {
    await Preferences.remove({ key: BIOMETRIC_EMAIL_KEY })
    await Preferences.remove({ key: BIOMETRIC_TOKEN_KEY })
  }

  /**
   * Autentica com biometria e retorna { email, refreshToken } se ok.
   * Lança erro se o usuário cancelar ou falhar.
   */
  async function authenticate(): Promise<{ email: string; refreshToken: string }> {
    const { value: email } = await Preferences.get({ key: BIOMETRIC_EMAIL_KEY })
    const { value: refreshToken } = await Preferences.get({ key: BIOMETRIC_TOKEN_KEY })

    if (!email || !refreshToken) {
      throw new Error('Biometria não configurada')
    }

    await NativeBiometric.verifyIdentity({
      reason: 'Faça login rapidamente com sua impressão digital',
      title: appName,
      subtitle: 'Autenticação biométrica',
    })

    return { email, refreshToken }
  }

  return {
    isAvailable,
    checkAvailability,
    isRegistered,
    register,
    unregister,
    authenticate,
  }
}
